import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

import { TicketMessageDetail } from '../core/models';

/** Spec, §Internal vs. public separation, point 2: the distinction is visible
 * for the whole time the message is displayed, never color-alone (WCAG,
 * §Accessibility) — an explicit text label backs the color/icon. */
@Component({
  selector: 'app-ticket-thread',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="thread" role="log" aria-live="polite" data-testid="ticket-thread">
      @for (message of messages; track message.id) {
        <div [attr.data-testid]="'message-' + message.id"
             class="row" [class.row--agent]="isAgent(message) && message.visibility !== 'internal'"
             [class.row--internal]="message.visibility === 'internal'"
             [class.message-public]="message.visibility === 'public'"
             [class.message-internal]="message.visibility === 'internal'">
          <div class="bubble"
               [class.bubble--public-agent]="isAgent(message) && message.visibility !== 'internal'"
               [class.bubble--public-customer]="!isAgent(message) && message.visibility !== 'internal'"
               [class.bubble--internal]="message.visibility === 'internal'">
            @if (message.visibility === 'internal') {
              <span [attr.data-testid]="'internal-label-' + message.id" class="internal-label">🔒 Internal — not visible to requester</span>
            }
            <span class="author">{{ authorLabel(message) }}</span>
            <p class="body">{{ message.body }}</p>
          </div>
        </div>
      } @empty {
        <div class="bt-empty"><span class="bt-empty__hint">No messages on this ticket yet.</span></div>
      }
    </div>
  `,
  styles: [`
    .thread { display: flex; flex-direction: column; gap: 0.75rem; }
    .row { display: flex; }
    .row--agent { justify-content: flex-end; }
    .row--internal { justify-content: center; }
    .bubble { max-width: 78%; padding: 0.6rem 0.85rem; border-radius: 12px; font-size: 14px; line-height: 1.5; }
    .bubble--public-customer { background: var(--bt-bg-surface-2); border: 1px solid var(--bt-border-light); border-top-left-radius: 4px; }
    .bubble--public-agent { background: color-mix(in srgb, var(--bt-brand-intercept) 14%, transparent); border: 1px solid color-mix(in srgb, var(--bt-brand-intercept) 30%, transparent); border-top-right-radius: 4px; }
    .bubble--internal { max-width: 92%; background: color-mix(in srgb, var(--bt-status-warning) 12%, transparent); border: 1px dashed color-mix(in srgb, var(--bt-status-warning) 55%, transparent); }
    .internal-label { display: block; font-size: 11px; font-weight: 700; color: color-mix(in srgb, var(--bt-status-warning) 80%, #6b4e00); margin-bottom: 0.25rem; }
    .author { display: block; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--bt-text-muted); margin-bottom: 0.15rem; }
    .body { margin: 0; color: var(--bt-text-primary); white-space: pre-wrap; }
  `],
})
export class TicketThreadComponent {
  @Input() messages: TicketMessageDetail[] = [];

  isAgent(message: TicketMessageDetail): boolean {
    return message.author_type !== 'customer';
  }

  authorLabel(message: TicketMessageDetail): string {
    switch (message.author_type) {
      case 'customer': return 'Requester';
      case 'agent': return 'Agent';
      case 'system': return 'System';
      case 'assistant': return 'Assistant';
      default: return message.author_type;
    }
  }
}
